/**
 * Client for `POST /api/intents/resolve-no-deposit-override` (B3 / 14ym9bv75mp):
 * Ops approves or declines a no-deposit request as ONE server transaction. The
 * server takes who decided from the login token, confirms the waived booking on
 * approve, and on decline sets the customer's flag and puts the $0 hold on the
 * pending deposit link sent here. Same fetch / auth / clock shape as
 * `@/api/opsIntents`: never throws; a failure carries `kind`. A replay on a
 * decided request is a 200 `alreadyResolved` no-op.
 */
import { authHeaders, isSigningIn, whenSignedIn } from '@/api/persist';
import { intentErrorKind, type IntentFailure } from '@/api/intents';
import { useSimulationStore } from '@/state/simulationStore';
import type { Deposit } from '@/domain/types';

export interface ResolveOverridePayload {
  requestId: string;
  approve: boolean;
  /** Ops's reason; required to decline. */
  note?: string;
  /** On decline: the pending link for the held booking (declinedHoldDepositLink). */
  deposit?: Deposit;
  nowIso?: string;
}

export type ResolveOverrideResult =
  | { ok: true; alreadyResolved: boolean; resolvedBy?: string; depositId?: string }
  | IntentFailure;

export async function resolveNoDepositOverrideIntent(payload: ResolveOverridePayload): Promise<ResolveOverrideResult> {
  if (isSigningIn()) await whenSignedIn(); // B3: never on a previous persona's token mid-switch
  let res: Response;
  try {
    res = await fetch('/api/intents/resolve-no-deposit-override', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...payload, nowIso: payload.nowIso ?? useSimulationStore.getState().clockIso }),
    });
  } catch {
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }
  const body = (await res.json().catch(() => undefined)) as
    | { alreadyResolved?: unknown; resolvedBy?: string; depositId?: string; error?: string; code?: string; message?: string }
    | undefined;
  if (res.ok) {
    return {
      ok: true,
      alreadyResolved: body?.alreadyResolved === true,
      ...(body?.resolvedBy ? { resolvedBy: body.resolvedBy } : {}),
      ...(body?.depositId ? { depositId: body.depositId } : {}),
    };
  }
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
  };
}
